import dynamic from 'next/dynamic';
import PropTypes from 'prop-types';
import { useMemo } from 'react';

import { isOfferFinished } from 'lib/offer';
import { offerQueryKeys } from 'lib/queryKeys';

import { getStoreOffersList } from 'services/store';

import TimelineNavigator from './TimelineNavigator';

import COUPONS from 'constants/coupons';
import { SORT_VALUES } from 'constants/sort';
import STORES from 'constants/store';

const OffersList = dynamic(() => import('./OffersList'));
const StoreCouponsList = dynamic(() => import('./CouponsList'));

const TimelineList = ({
  gaEventCategory,
  isOfferPage,
  serverCoupons = null,
  serverOffers = null,
  storeId,
  storeName,
  storeSlug,
  fixedOffer = null,
}) => {
  const couponsCount = useMemo(
    () =>
      serverCoupons?.coupons?.filter(
        (c) => c.couponStatusName !== COUPONS.STATUS.FINISHED
      )?.length,
    [serverCoupons?.coupons]
  );

  const offersCount = useMemo(
    () => serverOffers?.offers?.filter((o) => !isOfferFinished(o))?.length,
    [serverOffers?.offers]
  );

  const offersPageSize =
    storeSlug === STORES.MERCADO_LIVRE_STORE_SLUG
      ? STORES.MERCADO_LIVRE_OFFERS_SERVER_LIMIT
      : STORES.STORE_OFFERS_PAGE_SIZE;

  const hasMoreOffers = offersCount != null && offersCount >= offersPageSize;

  return (
    <>
      <TimelineNavigator
        className="mb-5 lg:mb-8"
        couponsCount={couponsCount}
        hasMoreOffers={hasMoreOffers}
        offersCount={offersCount}
        storeName={!isOfferPage ? storeName : undefined}
        storeSlug={storeSlug}
      />
      {isOfferPage ? (
        <OffersList
          fixedOffer={fixedOffer}
          gaEventCategory={gaEventCategory}
          initialOffers={serverOffers}
          queryFn={({ after }, opts) =>
            getStoreOffersList(
              {
                after,
                limit:
                  storeSlug === STORES.MERCADO_LIVRE_STORE_SLUG
                    ? STORES.MERCADO_LIVRE_OFFERS_SERVER_LIMIT
                    : STORES.STORE_OFFERS_PAGE_SIZE,
                sort: SORT_VALUES.LATEST,
                storeSlug,
              },
              opts
            )
          }
          queryKey={offerQueryKeys.listByStore({
            storeSlug,
            offersSort: SORT_VALUES.LATEST,
          })}
        />
      ) : (
        <StoreCouponsList
          gaEventCategory={gaEventCategory}
          serverCoupons={serverCoupons}
          storeId={storeId}
          storeName={storeName}
          storeSlug={storeSlug}
        />
      )}
    </>
  );
};

TimelineList.propTypes = {
  fixedOffer: PropTypes.object,
  gaEventCategory: PropTypes.string.isRequired,
  isOfferPage: PropTypes.bool.isRequired,
  serverCoupons: PropTypes.shape({
    after: PropTypes.string,
    coupons: PropTypes.arrayOf(
      PropTypes.shape({
        couponId: PropTypes.number.isRequired,
        couponDiscountValue: PropTypes.string.isRequired,
        couponDiscountShort: PropTypes.string.isRequired,
        couponDiscount: PropTypes.string.isRequired,
        couponDiscountOn: PropTypes.string.isRequired,
        couponStatusName: PropTypes.string.isRequired,
        couponInstructions: PropTypes.string.isRequired,
        couponVerified: PropTypes.oneOfType([PropTypes.bool, PropTypes.string]),
        storeImage: PropTypes.string,
        storeName: PropTypes.string.isRequired,
        storeDomain: PropTypes.string.isRequired,
        storeSlug: PropTypes.string.isRequired,
        couponTag: PropTypes.arrayOf(PropTypes.string.isRequired).isRequired,
        couponPublished: PropTypes.string.isRequired,
        couponTitle: PropTypes.string.isRequired,
        couponCode: PropTypes.string.isRequired,
        couponUrl: PropTypes.string.isRequired,
        userName: PropTypes.string,
      })
    ).isRequired,
  }),
  serverOffers: PropTypes.shape({
    after: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    sort: PropTypes.string,
    before: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    offers: PropTypes.arrayOf(
      PropTypes.shape({
        key: PropTypes.string.isRequired,
        offerComments: PropTypes.number.isRequired,
        offerId: PropTypes.number.isRequired,
        offerIsHighlight: PropTypes.bool.isRequired,
        ratings: PropTypes.shape().isRequired,
        offerOldPrice: PropTypes.number,
        offerPhoto: PropTypes.string.isRequired,
        offerPrice: PropTypes.number.isRequired,
        offerPriceType: PropTypes.string.isRequired,
        offerPublished: PropTypes.string.isRequired,
        offerSlug: PropTypes.string.isRequired,
        offerStatusName: PropTypes.string.isRequired,
        offerUserVisibility: PropTypes.string.isRequired,
        offerTags: PropTypes.arrayOf(
          PropTypes.shape({
            name: PropTypes.string.isRequired,
            type: PropTypes.string.isRequired,
          })
        ),
        offerTitle: PropTypes.string.isRequired,
        storeDomain: PropTypes.string.isRequired,
      }).isRequired
    ).isRequired,
  }),
  storeId: PropTypes.number.isRequired,
  storeName: PropTypes.string.isRequired,
  storeSlug: PropTypes.string.isRequired,
};

export default TimelineList;
